import React, { useMemo, useState, ChangeEvent } from "react"
import { Modal } from "react-bootstrap"
import Select, { createFilter } from "react-select"
import { useSelector } from "react-redux"
import "./components/modal/TestingImportPreviewModal.css"
import Navbar from "./components/Navbar"
import { useEvent, dispatchEvent } from "./hooks/useEvent"
import { Import, useImportList } from "./core/entities/import"
import { FacilityImportMap, useFacilityImportMapList } from "./core/entities/facilityImportMap"
import { ProviderImportMap, useProviderImportMapList } from "./core/entities/providerImportMap"
import { useFacilityList } from "./core/entities/facility"
import { ChargeImportOptions, TestingImportPreview, TestingImportPreviewRow, uploadTestingImportPreview, confirmTestingImport } from "./core/entities/testingImportPreview"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleCheck, faCircleXmark, faCircleNotch } from '@fortawesome/free-solid-svg-icons'
import { AppState } from "./reducers"
import { getTenantId } from "./common/jwt"

import FacilityDropdown from "./components/FacilityDropdown"
import ProviderDropdown from "./components/ProviderDropdown"

interface UploaderProps {
    onImportCreated: () => void
}

const Uploader = (props: UploaderProps) => {
    const [file, setFile] = useState<File | undefined>()
    const [isTesting, setIsTesting] = useState(true)
    const [err, setErr] = useState<string | undefined>()
    const [success, setSuccess] = useState<string | undefined>()
    const [preview, setPreview] = useState<TestingImportPreview | undefined>()
    const [importOptions, setImportOptions] = useState<ChargeImportOptions>({
        disable_auto_77427: false,
        disable_auto_igrt: false,
        clear_77427_77336_modifiers: false,
    })
    const isIndiana = getTenantId() === 4

    const handleImportOption = (key: keyof ChargeImportOptions) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setImportOptions({
            ...importOptions,
            [key]: e.currentTarget.checked,
        })
    }

    const handleSelectedFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFile(e.currentTarget.files![0])
    }

    const handleUpload = (endpoint: string) => async () => {
        setErr(undefined)
        setSuccess(undefined)

        try {
            if (isTesting) {
                const nextPreview = await uploadTestingImportPreview(`${endpoint}-testing-preview`, file!)
                setPreview(nextPreview)
            } else {
                const formData = new FormData();

                formData.append(
                    "myFile",
                    file!,
                    file!.name
                );

                if (isIndiana) {
                    Object.entries(importOptions).forEach(([key, value]) => formData.append(key, String(value)))
                }

                const resp = await fetch(endpoint, {
                    method: "POST",
                    body: formData,
                })

                if (resp.status !== 200) {
                    const json = await resp.json()

                    if ("error" in json) {
                        setErr(json.error)
                    }
                } else {
                    setSuccess(`Successfully uploaded file. Refresh the page to view status.`)
                    props.onImportCreated()
                }
            }
        } catch (e) {
            setErr(e instanceof Error ? e.message : "something went wrong")
        }
    }

    return <>
        {
            !preview
                ? null
                : <TestingImportPreviewModal
                    preview={preview}
                    importOptions={importOptions}
                    onHide={() => setPreview(undefined)}
                    onConfirmed={() => {
                        setPreview(undefined)
                        setSuccess("Import queued. Refresh the page to view status.")
                        props.onImportCreated()
                    }}
                />
        }
        {
            err
                ? <div className="alert alert-danger">{err}</div>
                : null
        }
        {
            success
                ? <div className="alert alert-success">{success}</div>
                : null
        }
        <input type="file" onChange={handleSelectedFile} />
        <label style={{ marginLeft: ".75em", marginRight: ".75em" }}>
            <input type="checkbox" checked={isTesting} onChange={(e) => setIsTesting(e.currentTarget.checked)} />
            <span style={{ marginLeft: ".35em" }}>Preview before import</span>
        </label>
        {
            !isIndiana
                ? null
                : <div className="card" style={{ marginTop: "1rem", marginBottom: "1rem", maxWidth: "48rem" }}>
                    <div className="card-body">
                        <h6 className="card-title">Indiana import options</h6>
                        <p className="card-text"><small>Optional settings apply to every charge in this uploaded file.</small></p>
                        <div className="form-check">
                            <input id="disableAuto77427" className="form-check-input" type="checkbox" checked={importOptions.disable_auto_77427} onChange={handleImportOption("disable_auto_77427")} />
                            <label htmlFor="disableAuto77427" className="form-check-label">Do not automatically add 77427</label>
                        </div>
                        <div className="form-check">
                            <input id="disableAutoIGRT" className="form-check-input" type="checkbox" checked={importOptions.disable_auto_igrt} onChange={handleImportOption("disable_auto_igrt")} />
                            <label htmlFor="disableAutoIGRT" className="form-check-label">Do not automatically add IGRT</label>
                        </div>
                        <div className="form-check">
                            <input id="clearManagementModifiers" className="form-check-input" type="checkbox" checked={importOptions.clear_77427_77336_modifiers} onChange={handleImportOption("clear_77427_77336_modifiers")} />
                            <label htmlFor="clearManagementModifiers" className="form-check-label">Remove modifiers from imported or automatically added 77427 and 77336</label>
                        </div>
                    </div>
                </div>
        }
        <button style={{ marginRight: ".5em" }} className="btn btn-primary" disabled={!file} onClick={handleUpload("/v2/imports/upload-charges")}>Upload Charges</button>
        <button style={{ marginRight: ".5em" }} className="btn btn-primary" disabled={!file} onClick={handleUpload("/v2/imports/upload-schedule")}>Upload Schedule</button>
    </>
}

interface TestingImportPreviewModalProps {
    preview: TestingImportPreview
    importOptions: ChargeImportOptions
    onHide: () => void
    onConfirmed: () => void
}

const TestingImportPreviewModal = (props: TestingImportPreviewModalProps) => {
    const [rows, setRows] = useState(props.preview.rows)
    const [sourceItems, setSourceItems] = useState(props.preview.source_items)
    const [saveMappings, setSaveMappings] = useState(true)
    const [saveFilters, setSaveFilters] = useState(true)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [err, setErr] = useState<string | undefined>()
    const { facilities } = useFacilityList()
    const cpts = useSelector((state: AppState) => state.cpt.data || [])
    const legacyFacilities = useSelector((state: AppState) => state.facility.data || [])
    const legacyProviders = useSelector((state: AppState) => state.physician.data || [])

    const detectedFacilityId = useMemo(() => {
        const facilityItem = sourceItems.find(item => item.kind === "facility" && item.facility_id)
        if (facilityItem?.facility_id) {
            return facilityItem.facility_id
        }

        const rowFacilityId = rows.find(row => row.facility_id)?.facility_id
        return rowFacilityId || props.preview.detected_facility_id
    }, [props.preview.detected_facility_id, rows, sourceItems])

    const detectedFacility = facilities.find(facility => facility.id === detectedFacilityId)
    const cptOptions = useMemo(() => {
        return cpts
            .filter((cpt: any) => !cpt?.attributes?.mod1)
            .map((cpt: any) => {
                let label = cpt.attributes.code

                if (cpt.attributes.mod1 && cpt.attributes.mod1.length) {
                    label += `-${cpt.attributes.mod1}`
                }

                if (cpt.attributes.abbr && cpt.attributes.abbr.length) {
                    label += ` (${cpt.attributes.abbr})`
                }

                return {
                    value: cpt.attributes.code,
                    label,
                }
            })
    }, [cpts])

    const isRowFiltered = (row: TestingImportPreviewRow) => {
        if (row.filtered) {
            return true
        }

        const activityItem = sourceItems.find(item => item.kind === "activity" && item.raw_value === row.raw_activity)
        if (activityItem?.filtered) {
            return true
        }

        const facilityItem = sourceItems.find(item => item.kind === "facility" && item.raw_value === row.raw_facility)
        if (facilityItem?.filtered) {
            return true
        }

        const providerItem = sourceItems.find(item => item.kind === "provider" && item.raw_value === row.raw_provider)
        return !!providerItem?.filtered
    }

    const getProviderPracticeIds = (providerID: number) => {
        const provider = legacyProviders.find((item: any) => Number(item.id) === providerID)
        return provider?.relationships?.practice?.data?.map((practice: any) => Number(practice.id)) || []
    }

    const getFacilityPracticeId = (facilityID: number) => {
        const facility = legacyFacilities.find((item: any) => Number(item.id) === facilityID)
        return Number(facility?.relationships?.practice?.data?.id || 0)
    }

    const inferFacilityIDForProvider = (providerID: number, nextSourceItems: typeof sourceItems) => {
        const providerPracticeIds = getProviderPracticeIds(providerID)
        if (!providerPracticeIds.length) {
            return 0
        }

        const mappedFacilityIds = Array.from(new Set(
            nextSourceItems
                .filter(item => item.kind === "facility" && item.facility_id)
                .map(item => item.facility_id)
                .filter((facilityID: number) => providerPracticeIds.includes(getFacilityPracticeId(facilityID)))
        ))

        if (mappedFacilityIds.length === 1) {
            return mappedFacilityIds[0]
        }

        const practiceFacilityIds = Array.from(new Set(
            legacyFacilities
                .filter((facility: any) => providerPracticeIds.includes(Number(facility?.relationships?.practice?.data?.id || 0)))
                .map((facility: any) => Number(facility.id))
        ))

        if (practiceFacilityIds.length === 1) {
            return practiceFacilityIds[0]
        }

        return 0
    }

    const reconcileProviderFacilityMappings = (nextSourceItems: typeof sourceItems, nextRows: typeof rows) => {
        const updatedSourceItems = [...nextSourceItems]
        const updatedRows = [...nextRows]

        updatedSourceItems
            .filter(item => item.kind === "provider" && item.provider_id)
            .forEach(providerItem => {
                const inferredFacilityID = inferFacilityIDForProvider(providerItem.provider_id, updatedSourceItems)
                if (!inferredFacilityID) {
                    return
                }

                const matchingRows = updatedRows.filter(row => row.raw_provider === providerItem.raw_value)
                const matchingFacilityValues = Array.from(new Set(
                    matchingRows
                        .map(row => row.raw_facility)
                        .filter(rawFacility => rawFacility && rawFacility.trim().length)
                ))

                updatedSourceItems.forEach((item, index) => {
                    if (item.kind !== "facility" || item.filtered || item.facility_id) {
                        return
                    }

                    if (!matchingFacilityValues.includes(item.raw_value)) {
                        return
                    }

                    updatedSourceItems[index] = {
                        ...item,
                        facility_id: inferredFacilityID,
                    }
                })

                matchingRows.forEach(row => {
                    const rowIndex = updatedRows.findIndex(candidate => candidate.id === row.id)
                    if (rowIndex === -1 || updatedRows[rowIndex].facility_id) {
                        return
                    }

                    updatedRows[rowIndex] = {
                        ...updatedRows[rowIndex],
                        facility_id: inferredFacilityID,
                    }
                })
            })

        return {
            sourceItems: updatedSourceItems,
            rows: updatedRows,
        }
    }

    const handleChangeSourceItemFilter = (index: number) => (e: ChangeEvent<HTMLInputElement>) => {
        const nextSourceItems = [...sourceItems]
        nextSourceItems[index] = {
            ...nextSourceItems[index],
            filtered: e.currentTarget.checked,
        }
        setSourceItems(nextSourceItems)
    }

    const handleChangeSourceItemFacility = (index: number) => (e: ChangeEvent<HTMLSelectElement>) => {
        const nextSourceItems = [...sourceItems]
        nextSourceItems[index] = {
            ...nextSourceItems[index],
            facility_id: Number(e.currentTarget.value),
            filtered: false,
        }
        const reconciled = reconcileProviderFacilityMappings(nextSourceItems, rows)
        setSourceItems(reconciled.sourceItems)
        setRows(reconciled.rows)
    }

    const handleChangeSourceItemProvider = (index: number) => (e: ChangeEvent<HTMLSelectElement>) => {
        const nextSourceItems = [...sourceItems]
        nextSourceItems[index] = {
            ...nextSourceItems[index],
            provider_id: Number(e.currentTarget.value),
            filtered: false,
        }
        const reconciled = reconcileProviderFacilityMappings(nextSourceItems, rows)
        setSourceItems(reconciled.sourceItems)
        setRows(reconciled.rows)
    }

    const handleChangeSourceItemCPT = (index: number) => (option: any) => {
        const nextValue = option?.value || ""
        const nextSourceItems = [...sourceItems]
        const item = nextSourceItems[index]

        nextSourceItems[index] = {
            ...item,
            cpt: nextValue,
        }
        setSourceItems(nextSourceItems)

        setRows(rows.map(row => {
            if (row.raw_activity !== item.raw_value) {
                return row
            }

            return {
                ...row,
                cpt: nextValue,
            }
        }))
    }

    const handleChangeRow = (rowID: number, key: "cpt" | "diagnosis") => (e: ChangeEvent<HTMLInputElement>) => {
        setRows(rows.map(row => {
            if (row.id !== rowID) {
                return row
            }

            return {
                ...row,
                [key]: e.currentTarget.value,
            }
        }))
    }

    const handleChangeRowFilter = (rowID: number) => (e: ChangeEvent<HTMLInputElement>) => {
        setRows(rows.map(row => {
            if (row.id !== rowID) {
                return row
            }

            return {
                ...row,
                filtered: e.currentTarget.checked,
            }
        }))
    }

    const hasMissingRequiredValues = rows.some(row => !isRowFiltered(row) && !row.cpt.trim())

    const handleConfirm = async () => {
        setErr(undefined)
        setIsSubmitting(true)

        try {
            await confirmTestingImport({
                filename: props.preview.filename,
                document_kind: props.preview.document_kind,
                source_context: props.preview.source_context,
                source_items: sourceItems,
                rows: rows.map(row => ({
                    ...row,
                    filtered: isRowFiltered(row),
                })),
                save_mappings: saveMappings,
                save_filters: saveFilters,
                options: props.importOptions,
            })

            props.onConfirmed()
        } catch (e) {
            setErr(e instanceof Error ? e.message : "something went wrong")
        } finally {
            setIsSubmitting(false)
        }
    }

    return <Modal show={true} dialogClassName="testing-import-preview-modal" onHide={props.onHide}>
        <div className="modal-content">
            <div className="modal-header">
                <h5 className="modal-title">Import Preview</h5>
            </div>
            <div className="modal-body testing-import-preview-modal-body">
                {
                    !err
                        ? null
                        : <div className="alert alert-danger">{err}</div>
                }
                <p><strong>Document type:</strong> {props.preview.document_kind}</p>
                <p><strong>Detected from:</strong> {props.preview.source_context.kind} = {props.preview.source_context.value || "unmapped"}</p>
                <p><strong>Detected facility:</strong> {detectedFacility ? detectedFacility.name : "Unmapped"}</p>
                <p><strong>Detected practice:</strong> {detectedFacility?.practice?.name || "Unmapped"}</p>

                <div className="form-check">
                    <input id="saveMappings" className="form-check-input" type="checkbox" checked={saveMappings} onChange={(e) => setSaveMappings(e.currentTarget.checked)} />
                    <label htmlFor="saveMappings" className="form-check-label">Save source mappings for future imports</label>
                </div>
                <div className="form-check" style={{ marginBottom: "1rem" }}>
                    <input id="saveFilters" className="form-check-input" type="checkbox" checked={saveFilters} onChange={(e) => setSaveFilters(e.currentTarget.checked)} />
                    <label htmlFor="saveFilters" className="form-check-label">Save filtered items for future imports from this detected source</label>
                </div>

                <h5>Source Items</h5>
                <div className="table-responsive">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Kind</th>
                                <th>Raw Value</th>
                                <th>Rows</th>
                                <th>Mapping</th>
                                <th>Filter</th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                sourceItems.map((item, index) => {
                                    return <tr key={`${item.kind}-${item.raw_value}`}>
                                        <td>{item.kind}</td>
                                        <td>{item.raw_value}</td>
                                        <td>{item.row_count}</td>
                                        <td>
                                            {
                                                item.kind === "facility"
                                                    ? <FacilityDropdown emptyOption="Unmapped" value={item.facility_id} onChange={handleChangeSourceItemFacility(index)} disabled={item.filtered} />
                                                    : item.kind === "provider"
                                                        ? <ProviderDropdown emptyOption="Unmapped" value={item.provider_id} onChange={handleChangeSourceItemProvider(index)} disabled={item.filtered} />
                                                        : <Select
                                                            filterOption={createFilter({ matchFrom: "start" })}
                                                            isClearable={true}
                                                            options={cptOptions}
                                                            value={cptOptions.find((option: any) => option.value === item.cpt) || null}
                                                            onChange={handleChangeSourceItemCPT(index)}
                                                            placeholder="Map to CPT"
                                                        />
                                            }
                                        </td>
                                        <td>
                                            <input type="checkbox" checked={item.filtered} onChange={handleChangeSourceItemFilter(index)} />
                                        </td>
                                    </tr>
                                })
                            }
                        </tbody>
                    </table>
                </div>

                <h5>Rows</h5>
                <div className="table-responsive">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Filter</th>
                                <th>Patient</th>
                                <th>Patient Match</th>
                                <th>DOS</th>
                                <th>Activity</th>
                                <th>CPT</th>
                                <th>Diagnosis</th>
                                <th>Facility</th>
                                <th>Provider</th>
                                <th>Modifiers</th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                rows.map(row => {
                                    return <tr key={row.id} style={{ opacity: isRowFiltered(row) ? 0.55 : 1 }}>
                                        <td><input type="checkbox" checked={row.filtered} onChange={handleChangeRowFilter(row.id)} /></td>
                                        <td>{row.last_name}, {row.first_name}<br /><small>{row.mrn}</small></td>
                                        <td>
                                            {
                                                row.patient_match_status === "matched"
                                                    ? <span className="badge bg-success">Matched</span>
                                                    : <span className="badge bg-warning text-dark">Will create</span>
                                            }
                                        </td>
                                        <td>{row.dos}</td>
                                        <td>{row.raw_activity || "Unmapped"}</td>
                                        <td><input className="form-control" type="text" value={row.cpt} onChange={handleChangeRow(row.id, "cpt")} /></td>
                                        <td><input className="form-control" type="text" value={row.diagnosis} onChange={handleChangeRow(row.id, "diagnosis")} /></td>
                                        <td>{row.raw_facility || "Unmapped"}</td>
                                        <td>{row.raw_provider || "Unmapped"}</td>
                                        <td>{row.modifiers.filter(Boolean).join(", ")}</td>
                                    </tr>
                                })
                            }
                        </tbody>
                    </table>
                </div>

                {
                    !hasMissingRequiredValues
                        ? null
                        : <div className="alert alert-warning">Every unfiltered row needs a CPT before you can confirm the import.</div>
                }
            </div>
            <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={props.onHide} disabled={isSubmitting}>Close</button>
                <button type="button" className="btn btn-primary" onClick={handleConfirm} disabled={isSubmitting || hasMissingRequiredValues}>Confirm Import</button>
            </div>
        </div>
    </Modal>
}

const SettingsModal = () => {
    const [isOpen, setIsOpen] = useState(false)
    const { facilityImportMaps, createFacilityImportMap, updateFacilityImportMap, deleteFacilityImportMap, err: facilityErr } = useFacilityImportMapList()
    const { providerImportMaps, createProviderImportMap, updateProviderImportMap, deleteProviderImportMap, err: providerErr } = useProviderImportMapList()

    useEvent("openImportListSettingsModal", () => {
        console.log("open modal")
        setIsOpen(true)
    })

    const handleChangeFacilityMapPattern = (m: FacilityImportMap) => (e: ChangeEvent<HTMLInputElement>) => {
        m.pattern = e.currentTarget.value
        updateFacilityImportMap(m)
    }

    const handleChangeFacilityMapFacility = (m: FacilityImportMap) => (e: ChangeEvent<HTMLSelectElement>) => {
        m.facility.id = Number(e.currentTarget.value)
        updateFacilityImportMap(m)
    }

    const handleDeleteFacilityMap = (m: FacilityImportMap) => () => {
        deleteFacilityImportMap(m)
    }

    const handleChangeProviderMapPattern = (m: ProviderImportMap) => (e: ChangeEvent<HTMLInputElement>) => {
        m.pattern = e.currentTarget.value
        updateProviderImportMap(m)
    }

    const handleChangeProviderMapProvider = (m: ProviderImportMap) => (e: ChangeEvent<HTMLSelectElement>) => {
        m.provider.id = Number(e.currentTarget.value)
        updateProviderImportMap(m)
    }

    const handleDeleteProviderMap = (m: ProviderImportMap) => () => {
        deleteProviderImportMap(m)
    }

    return <Modal show={isOpen} className="settings-modal">
        <div className="modal-content">
            <div className="modal-header">
                <h5 className="modal-title">Settings</h5>
            </div>
            <div className="modal-body">
                {
                    !facilityErr
                        ? null
                        : <div className="alert alert-danger">{facilityErr}</div>
                }
                {
                    !providerErr
                        ? null
                        : <div className="alert alert-danger">{providerErr}</div>
                }
                <p>All imports must be CSVs with fields in the order shown below. Fields can be empty (unless required), but they must exist.</p>
                <div className="row">

                    <div className="col-3">
                        <h5>Schedule Import Fields</h5>
                        <ul>
                            <li>Last Name (required)</li>
                            <li>First Name (required)</li>
                            <li>MRN (required)</li>
                            <li>DOS (required)</li>
                            <li>CPT (required)</li>
                            <li>Units (required)</li>
                            <li>Facility</li>
                            <li>Provider</li>
                            <li>Diagnosis</li>
                        </ul>

                    </div>
                    <div className="col-3">
                        <h5>Charge Import Fields</h5>
                        <ul>
                            <li>Last Name (required)</li>
                            <li>First Name (required)</li>
                            <li>MRN (required)</li>
                            <li>DOB (required)</li>
                            <li>DOS (required)</li>
                            <li>CPT (required)</li>
                            <li>Units (required)</li>
                            <li>Modifier</li>
                            <li>Facility</li>
                            <li>Provider</li>
                            <li>Diagnosis</li>
                        </ul>
                    </div>
                    <div className="col-3">
                        <h5>Preview Charge Import Fields</h5>
                        <ul>
                            <li>Last Name (required)</li>
                            <li>First Name (required)</li>
                            <li>MRN (required)</li>
                            <li>DOB (required)</li>
                            <li>DOS (required)</li>
                            <li>CPT (required)</li>
                            <li>Units (required)</li>
                            <li>Modifier 1</li>
                            <li>Modifier 2</li>
                            <li>Modifier 3</li>
                            <li>Modifier 4</li>
                            <li>Facility</li>
                            <li>Provider</li>
                            <li>Diagnosis</li>
                        </ul>
                        <p><small>Supports the standard 11-column layout with one modifier or this 14-column layout with four modifiers. Combined CPT input like <code>77300-26</code> is normalized into the modifier columns.</small></p>
                    </div>
                </div>
                <div className="row">
                    <div className="col-6">
                        <table className="table">
                            <thead>
                                <th>Pattern</th>
                                <th>Maps to Facility</th>
                                <th>Actions</th>
                            </thead>
                            <tbody>
                                {
                                    facilityImportMaps.map((m: FacilityImportMap) => {
                                        return <tr key={m.uuid}>
                                            <td><input className="form-control" type="text" defaultValue={m.pattern} onChange={handleChangeFacilityMapPattern(m)} /></td>
                                            <td><FacilityDropdown value={m.facility.id} onChange={handleChangeFacilityMapFacility(m)} /></td>
                                            <td><button className="btn btn-danger" onClick={handleDeleteFacilityMap(m)}>Delete</button></td>
                                        </tr>
                                    })
                                }
                                <tr>
                                    <td colSpan={3}><button className="btn btn-primary" onClick={() => { createFacilityImportMap() }}>Add</button></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div className="col-6">
                        <table className="table">
                            <thead>
                                <th>Pattern</th>
                                <th>Maps to Provider</th>
                                <th>Actions</th>
                            </thead>
                            <tbody>
                                {
                                    providerImportMaps.map((m: ProviderImportMap) => {
                                        return <tr>
                                            <td><input className="form-control" type="text" defaultValue={m.pattern} onChange={handleChangeProviderMapPattern(m)} /></td>
                                            <td><ProviderDropdown value={m.provider.id} onChange={handleChangeProviderMapProvider(m)} /></td>
                                            <td><button className="btn btn-danger" onClick={handleDeleteProviderMap(m)}>Delete</button></td>
                                        </tr>
                                    })
                                }
                                <tr>
                                    <td colSpan={3}><button className="btn btn-primary" onClick={() => { createProviderImportMap() }}>Add</button></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

            </div>
            <div className="modal-footer">
                {/* <button type="button" className="btn btn-primary">Save changes</button> */}
                <button type="button" className="btn btn-secondary" onClick={() => { setIsOpen(false) }}>Close</button>
            </div>
        </div>
    </Modal>
}

const ImportList = () => {
    const { imports, err, refetch } = useImportList()

    return <>
        <Navbar />
        <SettingsModal />
        <div style={{ marginTop: "50px" }}>
            {
                !err
                    ? <>
                        <div style={{ padding: "1em" }}>
                            <Uploader onImportCreated={refetch} />
                            <button className="btn btn-primary" onClick={() => { dispatchEvent("openImportListSettingsModal", {}) }}>Settings</button>
                        </div>
                        <table className="table">
                            <thead>
                                <th></th>
                                <th>Filename</th>
                                <th>File Rows</th>
                                <th>Imported Charges</th>
                                <th>Date Uploaded</th>
                                <th></th>
                            </thead>
                            <tbody>
                                {
                                    imports.map((imp: Import) => {
                                        return <ImportRow import={imp} />
                                    })
                                }
                            </tbody>
                        </table>
                    </>
                    : <div style={{ padding: "1em" }}>
                        <div className="alert alert-danger">{err}</div>
                    </div>
            }
        </div>
    </>
}

interface ImportRowProps {
    import: Import
}

const ImportRow = (props: ImportRowProps) => {
    const [showMessage, setShowMessage] = useState(false)

    return <>
        <tr key={props.import.id}>
            <td>
                <a onClick={() => setShowMessage(!showMessage)} style={{ opacity: props.import.status_message ? "0.75" : "0.2" }} role="button">
                    <img src="/right-arrow.png" width={16} className={"caret" + (showMessage ? " rotate-90" : "")} />
                </a>
            </td>
            <td>
                {props.import.filename}
                {
                    props.import.is_testing
                        ? <span className="badge bg-warning text-dark" style={{ marginLeft: ".5em" }}>Previewed</span>
                        : null
                }
            </td>
            <td>{props.import.rows}</td>
            <td>{props.import.charges}</td>
            <td>{props.import.date}</td>
            <td>
                {statusIcon(props.import.status.id)}
            </td>
        </tr>
        {
            showMessage && props.import.status_message
                ? <tr>
                    <td></td>
                    <td colSpan={5}>
                        <div className="alert alert-danger">{props.import.status_message}</div>
                    </td>
                </tr>
                : null
        }
    </>

}

const statusIcon = (statusId: number) => {
    switch (statusId) {
        case 2:
            return <FontAwesomeIcon color="#198754" icon={faCircleCheck} />
        case 3:
            return <FontAwesomeIcon color="#dc3545" icon={faCircleXmark} />
    }

    return <FontAwesomeIcon color="#0d6efd" icon={faCircleNotch} />
}


export default ImportList
